import React, {ChangeEvent, createRef, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import ReactSelect from 'react-select/async';

import {LoadingIndicator} from '../components/LoadingIndicator';
import {ActivityButton} from '../components/ActivityButton';
import {NavUserMaintenance} from '../components/NavUserMaintenance';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {
    Step,
    Form as FormType, StepAssignee
} from '../types';
import {Modal} from '../components/Modal';
import {User} from '../_services/User';
import {UserInfo} from '../components/UserInfo';

type AssigneeMaintenancePageProps = {};

type StepConfig = {
    // @ts-ignore
    attributes: string[];
    // @ts-ignore
    flowAdminInstructions?: string;
    [key: string]: StepAttribute;
};

type StepAttribute = {
    label: string;
    column: string;
};

const initialAssigneeStateMap = {
    editing: false,
    deleting: false,
    saving: false,
    values: {}
};

type ExportingState = {
    showModal: boolean;
    isExporting: boolean;
    isComplete: boolean;
    errorMessage: string;
    exportFileUrl: string;
};

const initialExportingState: ExportingState = {
    showModal: false,
    isExporting: false,
    isComplete: false,
    errorMessage: '',
    exportFileUrl: '',
};

/**
 * This page was thrown together and needs quite a bit of cleanup....
 *
 * @constructor
 */
export const AssigneeMaintenance: React.FunctionComponent<AssigneeMaintenancePageProps> = ({}) => {
    const {workflowId} = useParams();
    const history = useHistory();

    const newAssigneeInputRefs = useRef([]);

    const [pageIsLoading, setPageIsLoading]: any = useState(false);
    const [errorMessage, setErrorMessage]: any = useState('');

    const [form, setForm] = useState<FormType>();
    const [selectedAssigneeStep, setSelectedAssigneeStep] = useState<any>();
    const [assignees, setAssignees] = useState<StepAssignee[]>([]);
    const [stepConfig, setStepConfig] = useState<StepConfig>();
    const [selectedAssignees, setSelectedAssignees] = useState<number[]>([]);
    const [newAssigneeState, setNewAssigneeState] = useState<any>({});
    const [isSavingNewAssignee, setIsSavingNewAssignee] = useState<boolean>(false);

    const [isEditingAssigneeStateMap, setIsEditingAssigneeStateMap] = useState<any>({});

    const [exportingState, setExportingState] = useState<ExportingState>(initialExportingState);
    const [showFilterFunction, setShowFilterFunction] = useState<boolean>(false);

    const [filterFormState, setFilterFormState] = useState<object>({});

    useEffect(() => {
        Flow.getInstance().getForm(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setForm(response.json.form);
                } else {
                    throw Error(response?.json?.message || 'Error fetching Workflow');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching Workflow');
            })
            .finally(() => {
            });
    }, [workflowId]);

    useEffect(() => {
        // validate user has access to form
        if (form) {
            console.log('checking user access', form?.flow_form_admin_role, User.getInstance().hasRole(form.flow_form_admin_role));

            if (!(User.getInstance().isIsWfo() || (form?.flow_form_admin_role && User.getInstance().hasRole(form.flow_form_admin_role)))) {
                console.log('redirecting');
                history.replace('/');
            }
        }
    }, [form]);

    useEffect(() => {
        if (!selectedAssigneeStep) return;

        setPageIsLoading(true);
        let requestCount = 2;

        Flow.getInstance().getAssigneesForStep(selectedAssigneeStep.value)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setAssignees(response.json.assignees);
                } else {
                    throw Error(response?.json?.message || 'Error fetching Assignees');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching Assignees');
            })
            .finally(() => {
                if (--requestCount <= 0) {
                    setPageIsLoading(false);
                }
            });
        Flow.getInstance().getAssigneeStepConfig(selectedAssigneeStep.value)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setStepConfig(response.json.config);

                    // create array of refs for new assignee inputs, 1 for email and 1 for each of the attributes
                    newAssigneeInputRefs.current = [
                        // @ts-ignore - email ref
                        createRef(),
                        // @ts-ignore - attribute refs
                        ...(response.json.config.attributes.map(() => createRef())),
                    ];

                    // reset the new assignee inputs
                    setNewAssigneeState({});
                } else {
                    throw Error(response?.json?.message || 'Error fetching Assignees');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching Assignees');
            })
            .finally(() => {
                if (--requestCount <= 0) {
                    setPageIsLoading(false);
                }
            });

    }, [selectedAssigneeStep]);

    /**
     * Get available steps for a passed workflowId
     * @param workflowId
     */
    const handleOptionsAssigneeSteps = (workflowId: string) => () => {
        return Flow.getInstance().getAssigneeSteps(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    return response.json.steps.map((step: Step) => ({
                        label: step.name,
                        value: step.id
                    }))
                } else {
                    throw Error(response?.json?.mesasge || 'Error fetching workflow steps');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error fetching workflow steps');
            })
            .finally(() => {

            });
    };

    /**
     * Change Event Handler for Assignee Step dropdown
     * @param option
     */
    const handleChangeAssigneeStep = (option: any) => {
        console.log(option);
        setShowFilterFunction(false);
        setSelectedAssigneeStep(option);
        setAssignees([]);
        setStepConfig(undefined);
    };

    /**
     * Given a string of text to use as a filter, return a function to be passed to Array.filter
     * This function filters the assignee table
     * @param filter
     */
    const filterAssignees = (filterFormState: object) => (assignee: StepAssignee) => {
        const hasSearchValues = Object.keys(filterFormState).some((key: string) => filterFormState[key].trim() !== '');

        return (!hasSearchValues || (hasSearchValues && (
            Object.keys(filterFormState).every((key: string) => {
                try {
                    let val = filterFormState[key].toLowerCase();
                    if (val === '*') {
                        val = '\\*';
                    }
                    const filterRegexp = new RegExp(val);
                    return assignee.hasOwnProperty(key) && filterRegexp.test(assignee[key].toLowerCase())
                } catch (error) {
                }
                return false;
            })
        )))
    };

    /**
     * Change Event Handler for the Select All checkbox
     * @param checked
     * @param assignees
     */
    const handleSelectAllAssignees = (checked: boolean, assignees: StepAssignee[]): void => {
        if (checked) {
            setSelectedAssignees(assignees.map((assignee: StepAssignee) => assignee.id))
        } else {
            setSelectedAssignees([]);
        }
    };

    /**
     * Change Event Handler for individual assignee row checkboxes
     * @param assignee
     * @param isChecked
     */
    const handleAssigneeChecked = (assignee: StepAssignee, isChecked: boolean) => {
        if (isChecked) {
            setSelectedAssignees([
                ...selectedAssignees,
                assignee.id
            ])
        } else {
            const index = selectedAssignees.findIndex((assigneeId: number) => assignee.id === assigneeId);

            setSelectedAssignees([
                ...selectedAssignees.slice(0, index),
                ...selectedAssignees.slice(index + 1)
            ]);

        }
    };

    const handleAssigneeSave = (assignee: StepAssignee) => {
        setIsEditingAssigneeStateMap(state => ({
            ...state,
            [assignee.id]: {
                ...state[assignee.id],
                saving: true
            }
        }));

        Flow.getInstance().saveAssignee(isEditingAssigneeStateMap[assignee.id].values)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    const assignee = response.json.assignee;
                    const idx = assignees.findIndex((a: StepAssignee) => a.id === assignee.id);
                    setAssignees([
                        ...assignees.slice(0, idx),
                        assignee,
                        ...assignees.slice(idx + 1)
                    ]);
                } else {
                    throw Error(response?.json?.message || 'Error saving assignee');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error saving assignee');
            })
            .finally(() => {
                setIsEditingAssigneeStateMap(state => ({
                    ...state,
                    [assignee.id]: {
                        ...initialAssigneeStateMap,
                    }
                }));
            })
    };

    const handleAssigneeEdit = (assignee: StepAssignee) => {
        setIsEditingAssigneeStateMap(state => ({
            ...state,
            [assignee.id]: {
                ...state,
                editing: true,
                values: {
                    ...assignee
                }
            }
        }));
    };

    const handleAssigneeDelete = (assignee: StepAssignee) => {
        setIsEditingAssigneeStateMap(state => ({
            ...state,
            [assignee.id]: {
                ...state[assignee.id],
                deleting: true
            }
        }));

        Flow.getInstance().deleteAssignee(assignee.id)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    const idx = assignees.findIndex((a: StepAssignee) => a.id === assignee.id);
                    setAssignees([
                        ...assignees.slice(0, idx),
                        ...assignees.slice(idx + 1)
                    ]);
                } else {
                    throw Error(response?.json?.message || 'Error deleting');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error deleting');
            })
            .finally(() => {
                setIsEditingAssigneeStateMap(state => ({
                    ...state,
                    [assignee.id]: {
                        ...state[assignee.id],
                        deleting: false
                    }
                }));
            });
    };

    const handleAssigneeAdd = (stepId: string, newAssignees: any) => {
        setIsSavingNewAssignee(true);
        Flow.getInstance().addAssignees(stepId, newAssignees)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setAssignees([
                        ...(assignees ? assignees : []),
                        ...response.json.assignees
                    ]);
                    setNewAssigneeState(() => ({}));
                } else {
                    throw Error(response?.json?.message || 'Error adding assignee(s)');
                }
            })
            .catch((error: any) => {
                setErrorMessage(error?.message || error || 'Error adding assignee(s)');
            })
            .finally(() => {
                setIsSavingNewAssignee(false);
            });
    };

    const handleExport = (formId: any) => {
        setExportingState(() => ({
            ...initialExportingState,
            isExporting: true,
            showModal: true
        }));
        Flow.getInstance().exportAssigneesForWorkflow(formId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setExportingState((state: ExportingState) => ({
                        ...state,
                        isComplete: true,
                        exportFileUrl: response.json.file
                    }));
                } else {
                    throw Error(response?.json?.message || 'Error exporting. Please close and try again');
                }
            })
            .catch((error) => {
                setExportingState((state: ExportingState) => ({
                    ...state,
                    errorMessage: error?.message || error || 'Error exporting. Please close and try again'
                }));
            })
            .finally(() => {
                setExportingState((state: ExportingState) => ({
                    ...state,
                    isExporting: false
                }));
            })
    };

    /**
     * When pasting formatted text (fields tab-delimited, lines CR/LF-delimited), this function handles
     * parsing and saving the lines
     * @param pastedValue
     * @param stepAttributes
     */
    const handlePaste = (pastedValue: string, stepAttributes: string[]) => {
        const lines = pastedValue.split(/\r?\n/g);
        const data = lines.map((line: string) => {
            const cols = line.split(/\t/g);
            const lineData = stepAttributes.reduce((acc: any, item: any, index: number) => {
                return (index + 1) >= cols.length ?
                    acc
                    :
                    {
                        ...acc,
                        [item]: cols[index + 1]
                    }
            }, {userEmail: cols[0]});
            return lineData;
        });
        setNewAssigneeState(() => ({}));
        handleAssigneeAdd(selectedAssigneeStep.value, data);
    };

    const isEditingAssignee = (assignee: StepAssignee) => {
        return (isEditingAssigneeStateMap.hasOwnProperty(assignee.id) && isEditingAssigneeStateMap[assignee.id].editing);
    }
    const isSavingAssignee = (assignee: StepAssignee) => {
        return (isEditingAssigneeStateMap.hasOwnProperty(assignee.id) && isEditingAssigneeStateMap[assignee.id].saving);
    }
    const isDeletingAssignee = (assignee: StepAssignee) => {
        return (isEditingAssigneeStateMap.hasOwnProperty(assignee.id) && isEditingAssigneeStateMap[assignee.id].deleting);
    }

    const handleFilterValueChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilterFormState((state) => ({
            ...state,
            [field]: value
        }));
    };

    // sortAssignees - given the column to sort and a boolean to signifiy ascending
    //    sort null values to end
    const sortAssignees = (column: string, asc: boolean) => {
        switch (column) {
            case 'user_email':
                assignees.sort((a: any, b: any) => asc ?
                    (a.user_email === null) ? 1 : ((b.user_email === null) ? -1 : ((a.user_email === b.user_email) ? 0 : (a.user_email < b.user_email ? -1 : 1))) :
                    (a.user_email === null) ? 1 : ((b.user_email === null) ? -1 : ((a.user_email === b.user_email) ? 0 : (a.user_email < b.user_email ? 1 : -1))))
            break;
            case 'attribute_1':
                assignees.sort((a: any, b: any) => asc ?
                    (a.attribute_1 === null) ? 1 : ((b.attribute_1 === null) ? -1 : ((a.attribute_1 === b.attribute_1) ? 0 : (a.attribute_1 < b.attribute_1 ? -1 : 1))) :
                    (a.attribute_1 === null) ? 1 : ((b.attribute_1 === null) ? -1 : ((a.attribute_1 === b.attribute_1) ? 0 : (a.attribute_1 < b.attribute_1 ? 1 : -1))))
            break;
            case 'attribute_2':
                assignees.sort((a: any, b: any) => asc ?
                    (a.attribute_2 === null) ? 1 : ((b.attribute_2 === null) ? -1 : ((a.attribute_2 === b.attribute_2) ? 0 : (a.attribute_2 < b.attribute_2 ? -1 : 1))) :
                    (a.attribute_2 === null) ? 1 : ((b.attribute_2 === null) ? -1 : ((a.attribute_2 === b.attribute_2) ? 0 : (a.attribute_2 < b.attribute_2 ? 1 : -1))))
            break;
            case 'attribute_3':
                assignees.sort((a: any, b: any) => asc ?
                    (a.attribute_3 === null) ? 1 : ((b.attribute_3 === null) ? -1 : ((a.attribute_3 === b.attribute_3) ? 0 : (a.attribute_3 < b.attribute_3 ? -1 : 1))) :
                    (a.attribute_3 === null) ? 1 : ((b.attribute_3 === null) ? -1 : ((a.attribute_3 === b.attribute_3) ? 0 : (a.attribute_3 < b.attribute_3 ? 1 : -1))))
            break;
            case 'attribute_4':
                assignees.sort((a: any, b: any) => asc ?
                    (a.attribute_4 === null) ? 1 : ((b.attribute_4 === null) ? -1 : ((a.attribute_4 === b.attribute_4) ? 0 : (a.attribute_4 < b.attribute_4 ? -1 : 1))) :
                    (a.attribute_4 === null) ? 1 : ((b.attribute_4 === null) ? -1 : ((a.attribute_4 === b.attribute_4) ? 0 : (a.attribute_4 < b.attribute_4 ? 1 : -1))))
            break;
            case 'attribute_5':
                assignees.sort((a: any, b: any) => asc ?
                    (a.attribute_5 === null) ? 1 : ((b.attribute_5 === null) ? -1 : ((a.attribute_5 === b.attribute_5) ? 0 : (a.attribute_5 < b.attribute_5 ? -1 : 1))) :
                    (a.attribute_5 === null) ? 1 : ((b.attribute_5 === null) ? -1 : ((a.attribute_5 === b.attribute_5) ? 0 : (a.attribute_5 < b.attribute_5 ? 1 : -1))))
            break;
            case 'attribute_6':
                assignees.sort((a: any, b: any) => asc ?
                    (a.attribute_ === null) ? 1 : ((b.attribute_6 === null) ? -1 : ((a.attribute_6 === b.attribute_6) ? 0 : (a.attribute_6 < b.attribute_6 ? -1 : 1))) :
                    (a.attribute_6 === null) ? 1 : ((b.attribute_6 === null) ? -1 : ((a.attribute_6 === b.attribute_6) ? 0 : (a.attribute_6 < b.attribute_6 ? 1 : -1))))
                break;
            default:
                return
        }
    };

    const displayAssignee = (assignee: StepAssignee) => {
        return <UserInfo displayName={assignee?.userDetail?.displayName || ''}
                         email={assignee.user_email}
                         title={assignee?.userDetail?.title || ''}
                         department={assignee?.userDetail?.department || ''}
                         profilePhoto={assignee?.userDetail?.profilePhoto || ''}
        />
    }

    if (!form) {
        return <LoadingIndicator/>;
    }

    return (
        <div className="row">
            <div className="col-12">
                {form && <h1 className={'abb-header'}>{form.title}</h1>}
                <h2>Step Assignees</h2>
            </div>
            <div className="col-12">
            {workflowId && <NavUserMaintenance workflowId={workflowId}/>}
            </div>
            <div className="col-12 p-4">
            {errorMessage && <p className="alert alert-danger">{errorMessage}</p>}
            {pageIsLoading && <LoadingIndicator/>}

            <div className="row justify-content-between">
                <div className="col-6">
                    <ReactSelect
                        placeholder="Select a Workflow Step..."
                        required={true}
                        isClearable={true}
                        loadOptions={handleOptionsAssigneeSteps(workflowId as string)}
                        defaultOptions
                        value={selectedAssigneeStep}
                        onChange={handleChangeAssigneeStep}
                    />
                    {selectedAssigneeStep && stepConfig?.flowFilterFunction &&
                    <button type="button"
                            className="btn btn-link btn-sm"
                            onClick={() => {
                                setShowFilterFunction(!showFilterFunction);
                            }}
                    >{showFilterFunction ? 'Hide' : 'Show'} Filter Function</button>
                    }
                </div>
                {form &&
                <div className="col text-right">
                    <button type="button"
                            className="btn btn-primary mt-2"
                            onClick={() => {
                                handleExport(form.id);
                            }}
                    >Export All Assignees
                    </button>
                </div>
                }
            </div>

            {!selectedAssigneeStep && <p><em>Select a Workflow Step to List Assignees</em></p>}

            {showFilterFunction && stepConfig?.flowFilterFunction &&
            <code id="assignee-filter-function">
                {stepConfig.flowFilterFunction}
            </code>
            }

            {stepConfig?.flowAdminInstructions &&
            <div dangerouslySetInnerHTML={{__html: stepConfig.flowAdminInstructions}} />
            }

            {assignees && stepConfig &&
            <div className="row mt-2">
                <div className="col-12">
                    <table className="table table-sm">
                        <thead>
                        <tr>
                            <th style={{width: '25px'}}>
                                {/*
                                <input type="checkbox"
                                       checked={selectedAssignees.length === assignees.length}
                                       onChange={(e) => {
                                           handleSelectAllAssignees(e.target.checked, assignees);
                                       }}
                                />
                                */}
                            </th>
                            <th
                                className="dsc"
                                onClick={(e) => {
                                    sortAssignees("user_email", e.currentTarget.className  === "dsc");
                                    setAssignees([
                                       ...(assignees ? assignees : [])
                                    ]);
                                    // toggle class to indicate the current state of the column 
                                    e.currentTarget.className = e.currentTarget.className  === "dsc" ? "asc" : "dsc"
                                }} >
                                Assignee Email
                                {assignees?.length > 0 &&
                                <input type="text"
                                       className="form-control form-control-sm"
                                       value={filterFormState?.user_email || ''}
                                       onClick={(e) => e.stopPropagation()}
                                       onChange={handleFilterValueChange('user_email')}
                                   />
                                }
                            </th>
                            {stepConfig.attributes.map((attribute: string, index: number) => (
                                <th
                                   className="dsc"
                                    onClick={(e) => {
                                        sortAssignees(stepConfig[attribute].column, e.currentTarget.className  === "dsc");
                                        setAssignees([
                                            ...(assignees ? assignees : [])
                                        ]);
                                        // toggle class to indicate the current state of the column 
                                        e.currentTarget.className = e.currentTarget.className  === "dsc" ? "asc" : "dsc"                                    }}

                                    key={`assigneeHeader-${attribute}-${index}`}>
                                    {stepConfig[attribute].label}
                                    {assignees?.length > 0 &&
                                        <input type="text"
                                               className="form-control form-control-sm"
                                               value={filterFormState?.[stepConfig[attribute].column] || ''}
                                               onClick={(e) => e.stopPropagation()}
                                               onChange={handleFilterValueChange(stepConfig[attribute].column)}
                                        />
                                    }
                                </th>
                            ))}
                            <th style={{width: '100px', whiteSpace: 'nowrap'}}/>
                        </tr>
                        </thead>
                        <tbody>
                        {assignees.filter(filterAssignees(filterFormState)).map((assignee: StepAssignee) => (
                            <tr key={assignee.id}>
                                <td>
                                    {isEditingAssignee(assignee) ?
                                        <div/>
                                        :
                                        <>
                                        {/*
                                        <input type="checkbox"
                                               checked={selectedAssignees.some((assigneeId: number) => assignee.id === assigneeId)}
                                               onChange={(e) => {
                                                   handleAssigneeChecked(assignee, e.target.checked)
                                               }}
                                        />
                                        */}
                                        </>
                                    }
                                </td>
                                <td>
                                    {isEditingAssignee(assignee) ?
                                        <input type="text"
                                               className="form-control"
                                               value={isEditingAssigneeStateMap[assignee.id].values.user_email}
                                               onChange={(e) => {
                                                   setIsEditingAssigneeStateMap({
                                                       ...isEditingAssigneeStateMap,
                                                       [assignee.id]: {
                                                           ...isEditingAssigneeStateMap[assignee.id],
                                                           values: {
                                                               ...isEditingAssigneeStateMap[assignee.id].values,
                                                               user_email: e.target.value
                                                           }
                                                       }
                                                   })
                                               }}
                                        />
                                        :
                                        displayAssignee(assignee)
                                    }
                                </td>
                                {stepConfig.attributes.map((attribute: string, index: number) => (
                                    <td key={`assignee-${attribute}-${index}`}>
                                        {isEditingAssignee(assignee) ?
                                            <input type="text"
                                                   className="form-control"
                                                   value={isEditingAssigneeStateMap[assignee.id].values[stepConfig[attribute].column]}
                                                   onChange={(e) => {
                                                       setIsEditingAssigneeStateMap({
                                                           ...isEditingAssigneeStateMap,
                                                           [assignee.id]: {
                                                               ...isEditingAssigneeStateMap[assignee.id],
                                                               values: {
                                                                   ...isEditingAssigneeStateMap[assignee.id].values,
                                                                   [stepConfig[attribute].column]: e.target.value
                                                               }
                                                           }
                                                       })
                                                   }}
                                            />
                                            :
                                            assignee[stepConfig[attribute].column]
                                        }
                                    </td>
                                ))}
                                <td style={{whiteSpace: 'nowrap'}}>
                                    {isEditingAssignee(assignee) ?
                                        isSavingAssignee(assignee) ?
                                            <i className="fas fa-spinner fa-spin"/>
                                            :
                                            <div>
                                                <button type="button"
                                                        className="btn btn-link btn-sm"
                                                        onClick={() => {
                                                            handleAssigneeSave(assignee)
                                                        }}
                                                >Save
                                                </button>
                                                <button type="button"
                                                        className="btn btn-link btn-sm"
                                                        onClick={() => {
                                                            setIsEditingAssigneeStateMap({
                                                                ...isEditingAssigneeStateMap,
                                                                [assignee.id]: {...initialAssigneeStateMap}
                                                            })
                                                        }}
                                                >Cancel
                                                </button>
                                            </div>
                                        :
                                        <div>
                                            <button type="button"
                                                    className="btn btn-link btn-sm"
                                                    disabled={isDeletingAssignee(assignee)}
                                                    onClick={() => {
                                                        handleAssigneeEdit(assignee);
                                                    }}
                                            >Edit
                                            </button>
                                            <button type="button"
                                                    className="btn btn-link btn-sm text-danger"
                                                    disabled={isDeletingAssignee(assignee)}
                                                    onClick={() => {
                                                        handleAssigneeDelete(assignee);
                                                    }}
                                            >Delete
                                            </button>
                                        </div>
                                    }
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td/>
                            <td>
                                <textarea rows={1}
                                          className="form-control"
                                          style={{height: 'calc(1.8em + .75rem + 2px)'}}
                                          disabled={isSavingNewAssignee}
                                          ref={newAssigneeInputRefs.current[0]}
                                          value={newAssigneeState?.userEmail || ''}
                                          onChange={(e) => {
                                              if (newAssigneeState?.isPasting) {
                                                  handlePaste(e.target.value, stepConfig.attributes);
                                              } else {
                                                  const value = e.target.value;
                                                  setNewAssigneeState((prevState: any) => ({
                                                      ...prevState,
                                                      userEmail: value
                                                  }))
                                              }
                                          }}
                                          onPaste={() => {
                                              setNewAssigneeState((prevState: any) => ({
                                                  ...prevState,
                                                  isPasting: true
                                              }));
                                          }}
                                          onKeyDown={(e) => {
                                              if (e.keyCode === 13) {
                                                  if (newAssigneeInputRefs.current.length > 1) {
                                                      // @ts-ignore
                                                      newAssigneeInputRefs.current[1].current.focus();
                                                  } else {
                                                      handleAssigneeAdd(selectedAssigneeStep.value, [newAssigneeState]);
                                                  }
                                              }
                                          }}
                                />
                            </td>
                            {stepConfig.attributes.map((attribute: string, index: number) => (
                                <td key={`new-${attribute}-${index}`}>
                                    <input type="text"
                                           className="form-control"
                                           disabled={isSavingNewAssignee}
                                           ref={newAssigneeInputRefs.current[index + 1]}
                                           value={newAssigneeState.hasOwnProperty(attribute) ? newAssigneeState[attribute] : ''}
                                           onChange={(e) => {
                                               const value = e.target.value;
                                               setNewAssigneeState((prevState: any) => ({
                                                   ...prevState,
                                                   [attribute]: value
                                               }))
                                           }}
                                           onKeyDown={(e) => {
                                               if (e.keyCode === 9) {
                                                   /* handle tab */
                                                   if ((index + 2) >= newAssigneeInputRefs.current.length) {
                                                       handleAssigneeAdd(selectedAssigneeStep.value, [newAssigneeState]);
                                                   } else {
                                                       // @ts-ignore
                                                       newAssigneeInputRefs.current[index + 2].current.focus();
                                                   }
                                               } else if (e.keyCode === 13) {
                                                   /* handle enter */
                                                   handleAssigneeAdd(selectedAssigneeStep.value, [newAssigneeState]);
                                               }
                                           }}
                                    />
                                </td>
                            ))}
                            <td>
                                {isSavingNewAssignee ?
                                    <i className="fas fa-spinner fa-spin"/>
                                    :
                                    <button type="button"
                                            className="btn btn-link btn-sm"
                                            onClick={() => {
                                                handleAssigneeAdd(selectedAssigneeStep.value, [newAssigneeState]);
                                            }}
                                    >Add
                                    </button>
                                }
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    {false && selectedAssignees.length > 0 &&
                    <ActivityButton type="button"
                                    className="btn btn-sm btn-secondary"
                                    isActive={false}
                                    onClick={() => {
                                    }}
                    >Delete Selected</ActivityButton>
                    }
                </div>
            </div>
            }
            </div>

            {exportingState.showModal &&
            <Modal>
                <div className="modal-header">
                    <h5 className="modal-title">Exporting Assignees</h5>
                    <button type="button"
                            className="close"
                            aria-label="Close"
                            tabIndex={-1}
                            onClick={() => {
                                setExportingState((state: ExportingState) => ({
                                    ...state,
                                    showModal: false
                                }));
                            }}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
                <div className="modal-body">
                    <div className="container">
                        <div className="row mb-2">
                            <div className="col">
                                {exportingState.isComplete &&
                                <div>
                                    <p>Export complete. Download by clicking the link below.</p>
                                    <p><a href={exportingState.exportFileUrl}>Download File</a></p>
                                </div>
                                }
                                {exportingState.isExporting &&
                                <div className="d-flex">
                                    <div><i className="fas fa-spinner fa-spin"/></div>
                                    <span className="ml-2 flex-grow-1">Please wait, export is running. Once complete the link to download will appear.</span>
                                </div>
                                }
                                {exportingState.errorMessage && <p className="text-danger">{exportingState.errorMessage}</p>}
                            </div>
                        </div>
                    </div>
                </div>
                {exportingState.isComplete &&
                <div className="modal-footer">
                    <button type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setExportingState((state: ExportingState) => ({
                                    ...state,
                                    showModal: false
                                }));
                            }}
                    >Close
                    </button>
                </div>
                }
            </Modal>
            }
        </div>
    );
};